/* Copyright (c) 2024 Xperi. All Rights Reserved. */
import { FunctionComponent, useEffect, useRef, useState } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import {
    ActionKernelType,
    ContentScreenDetails,
    Detail,
    GetThisShowDetailsPresentation,
    UpcomingEpisodesDetailsPresentation,
} from 'client-code/actions';

import { DeviceModel } from '../../core/device';
import { BrandingUiInfoKeys } from '../../core/entities';
import { ContentType } from '../../core/enums/ContentType';
import { NdvrInteractor, VodInteractor } from '../../core/interactors';
import { Locale, LocaleKeys } from '../../core/locale';
import { Log } from '../../core/logging';
import { CursorManager, KeyManager, ScreenTaskControllerTypes } from '../../core/navigation';
import { ScreenController } from '../../core/navigation/ScreenController';
import { NetworkManager } from '../../core/network/NetworkManager';
import { useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { TProgramScreenState } from '../../core/types/TProgramScreenState';
import { UIOrchestrator, UIOrchestratorState } from '../../core/uiorchestrator';
import { BrandingUiUtility } from '../../core/utils';
import { hideOverlayDialog } from '../../store/overlaydialog/overlaydialog.reducers';
import { initAndResetBiaxialVerticalStrip } from '../../store/programscreen/programscreen.reducers';
import { getRecordingsStatus } from '../../store/recordings/recordings.reducers';
import { setScreenTaskController } from '../../store/screens/screens.reducers';
import { hideWhisper, showWhisper } from '../../store/whisper/whisper.reducer';
import { ClockTypes } from '../Clock';
import { AntiburnOsd, ScreenHeader } from '../common';
import { Whisper } from '../Whisper';
import { BiaxialActionWidget } from './BiaxialActionWidget';
import { ContentScreenPresentationId } from './ContentScreenPresentationId';
import { InteractiveLayer } from './InteractiveLayer';
import { ProgramScreenHelper } from './ProgramScreenHelper';

import './ProgramScreen.scss';

const ProgramScreen: FunctionComponent = (): JSX.Element => {
    const TAG = 'ProgramScreen';

    const dispatch = useTivoDispatch();

    // listening to url change events as a trigger to refresh the screen
    const url = useTivoSelector((state) => state.urlChangeEvents.url);

    // global state listeners
    const isAntiBurnOsdActive = useTivoSelector((state) => state.antiburnOsd.isActive);

    // screen local states
    const [screenTitle, setScreenTitle] = useState<{ title: string; titleSuffix: string }>({
        title: '',
        titleSuffix: '',
    });
    const [actionList, setActionList] = useState<Array<Detail>>([]);
    const [isCursorActive, setIsCursorActive] = useState<boolean>(CursorManager.isCursorAppeared());
    const [unifiedCollectionId, setUnifiedCollectionId] = useState<string>('');
    const [_contentId, setContentId] = useState<string>('');
    const [layoutType, setLayoutType] = useState<string>('');
    // screen refs
    const programScreenInfo = useRef<{
        id: string;
        contentType: ContentType;
        partnerId?: string;
        collapsedVodItemId?: string;
        contentSupplierPartnerId?: string;
        targetInitialProgramScreenOption?: ContentScreenPresentationId;
    }>({ id: '', contentType: '' as ContentType });
    const initialHorizontalHighlightIndex = useRef<number>(0);
    const initialVerticalHighlightIndex = useRef<number>(0);
    const isInitialCallFromURL = useRef<boolean>(true);

    const fetchContentScreenDetails = async (contentInfo: {
        id: string;
        contentType: ContentType;
        partnerId?: string;
        collapsedVodItemId?: string;
        contentSupplierPartnerId?: string;
        targetInitialProgramScreenOption?: ContentScreenPresentationId;
    }) => {
        const { id, contentType, partnerId, collapsedVodItemId, contentSupplierPartnerId } =
            contentInfo;
        try {
            let contentScreenDetails = {} as ContentScreenDetails;
            const ndvrServices = new NdvrInteractor();
            const vodServices = new VodInteractor();
            switch (contentType) {
                case ContentType.RECORDING_ID:
                    contentScreenDetails =
                        await ndvrServices.getContentScreenDetailsRecordingActions({
                            recordingId: id,
                        });
                    break;
                case ContentType.CONTENT_ID:
                    contentScreenDetails =
                        await vodServices.getContentScreenDetailsContentActionsV2({
                            contentId: id,
                            screenType: 'ProgramScreen',
                            partnerId,
                            collapsedVodItemId,
                            contentSupplierPartnerId,
                        });
                    break;
                case ContentType.OFFER_ID:
                    contentScreenDetails = await ndvrServices.getContentScreenDetailsOfferActions({
                        offerId: id,
                    });
                    break;
                case ContentType.VOD_OFFER_ID:
                    contentScreenDetails =
                        await ndvrServices.getContentScreenDetailsVodOfferActionsV2({
                            vodOfferId: id,
                            collapsedVodItemId,
                            screenType: 'ProgramScreen',
                        });
                    break;
                default:
            }

            const { collectionId, contentId, contentScreenType } = contentScreenDetails;
            setLayoutType(contentScreenType);
            setUnifiedCollectionId(collectionId!);
            setContentId(contentId!);
            setScreenTitle(ProgramScreenHelper.generateScreenTitle(contentScreenDetails));
            const optionActions = contentScreenDetails.detailList.details.filter(
                (detail) => detail.presentation?.presentationId,
            );

            if (contentInfo.targetInitialProgramScreenOption && isInitialCallFromURL.current) {
                const index = ProgramScreenHelper.calculateInitialVerticalIndex(
                    contentInfo.targetInitialProgramScreenOption,
                    optionActions,
                );
                if (index !== -1) {
                    initialVerticalHighlightIndex.current = index;
                    isInitialCallFromURL.current = false;
                    ProgramScreenHelper.resetTargetInitialProgramScreenOption();
                    programScreenInfo.current = {
                        ...programScreenInfo.current,
                        targetInitialProgramScreenOption: undefined,
                    };
                }
            }

            setActionList(optionActions);
            return optionActions;
        } catch (_error) {
            const errorMessage = _error instanceof Error ? _error.message : JSON.stringify(_error);
            Log.e(TAG, errorMessage);
            Diagnostics.error(TAG, errorMessage);
            setActionList([]);
            return [];
        } finally {
            if (KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().stopKeyIgnoring();
        }
    };

    const onCursorStateChangeCallback = (state: boolean) => {
        setIsCursorActive(state);
    };

    const getDisplayOptionsFromActions = (options: Detail[]): Array<string> => {
        return options.map((optionItem: Detail) => {
            const { presentation } = optionItem;
            if (!presentation || !presentation.presentationId) {
                return '';
            }
            switch (presentation.presentationId) {
                case ContentScreenPresentationId.GET_THIS_SHOW_DETAIL: {
                    const getThisShowDetailsPresentation =
                        presentation as GetThisShowDetailsPresentation;
                    if (getThisShowDetailsPresentation.canRecord) {
                        return `${Locale.getString(
                            LocaleKeys[
                                `PS_OPTION_CAN_RECORD_${presentation.presentationId}${getThisShowDetailsPresentation.contentType ? `_${getThisShowDetailsPresentation.contentType.toUpperCase()}` : ''}`
                            ],
                        )}`;
                    }
                    return `${Locale.getString(
                        LocaleKeys[
                            `PS_OPTION_${presentation.presentationId}${getThisShowDetailsPresentation.contentType ? `_${getThisShowDetailsPresentation.contentType.toUpperCase()}` : ''}`
                        ],
                    )}`;
                }
                case ContentScreenPresentationId.UPCOMING_EPISODES_DETAIL: {
                    const upcomingEpisodesDetailsPresentation =
                        presentation as UpcomingEpisodesDetailsPresentation;
                    return `${Locale.getString(
                        LocaleKeys[
                            `PS_OPTION_${presentation.presentationId}${upcomingEpisodesDetailsPresentation.contentType ? `_${upcomingEpisodesDetailsPresentation.contentType.toUpperCase()}` : ''}`
                        ],
                    )} ${upcomingEpisodesDetailsPresentation.hasUpcomingCollectionEpisodes ? '' : `(${Locale.getString(LocaleKeys.NONE)})`}`;
                }
                case ContentScreenPresentationId.CAST_DETAIL:
                case ContentScreenPresentationId.ONE_PASS_OPTIONS_DETAIL:
                case ContentScreenPresentationId.RECORDING_OPTIONS_DETAIL:
                case ContentScreenPresentationId.RECOVER_RECORDING_DETAIL:
                case ContentScreenPresentationId.SEASON_OR_YEAR_DETAIL:
                case ContentScreenPresentationId.UPCOMING_AIRING_DETAIL:
                case ContentScreenPresentationId.WATCH_NOW_DETAIL:
                case ContentScreenPresentationId.INFO_DETAIL:
                case ContentScreenPresentationId.MORE_INFO_DETAIL:
                case ContentScreenPresentationId.ON_DEMAND_EPISODES_DETAIL:
                case ContentScreenPresentationId.WATCH_PREVIEW_DETAIL:
                    return Locale.getString(LocaleKeys[`PS_OPTION_${presentation.presentationId}`]);
                default:
                    return '';
            }
        });
    };

    const displayOptionNames = getDisplayOptionsFromActions(actionList);

    const fetchActionsAndRefreshProgramScreen = async (state: UIOrchestratorState) => {
        try {
            const updatedActionList = await fetchContentScreenDetails(programScreenInfo.current);
            if (
                state === UIOrchestratorState.RECORDING_DELETED ||
                state === UIOrchestratorState.RECORDING_CREATED ||
                state === UIOrchestratorState.BOOKMARK_DELETED
            ) {
                refreshProgramScreen(updatedActionList);
            } else {
                checkRefreshProgramScreen(state, updatedActionList);
            }
        } catch (_error) {
            const errorMessage = _error instanceof Error ? _error.message : JSON.stringify(_error);
            Log.e(TAG, errorMessage);
            Diagnostics.error(TAG, errorMessage);
        }
    };

    const handleRecordingStatusUpdate = (state: UIOrchestratorState) => {
        switch (state) {
            case UIOrchestratorState.RECORDING_CREATED:
            case UIOrchestratorState.RECORDING_DELETED:
            case UIOrchestratorState.RECORDING_RECOVERED:
            case UIOrchestratorState.ONE_PASS_CREATED:
            case UIOrchestratorState.ONE_PASS_DELETED:
            case UIOrchestratorState.ONE_PASS_MODIFIED:
                dispatch(getRecordingsStatus());
                break;
            default:
        }
    };

    const handleDataRefresh = (state: UIOrchestratorState) => {
        switch (state) {
            case UIOrchestratorState.ONE_PASS_CREATED:
            case UIOrchestratorState.ONE_PASS_DELETED:
            case UIOrchestratorState.BOOKMARK_DELETED:
            case UIOrchestratorState.NEW_SCREEN_MOUNTED:
            case UIOrchestratorState.RECORDING_CREATED:
            case UIOrchestratorState.RECORDING_DELETED: {
                fetchActionsAndRefreshProgramScreen(state);
                break;
            }

            case UIOrchestratorState.ONE_PASS_MODIFIED:
            case UIOrchestratorState.RECORDING_MODIFIED:
            case UIOrchestratorState.BOOKMARK_CREATED:
            case UIOrchestratorState.RECORDING_PERSISTENCE_MODIFIED: {
                fetchContentScreenDetails(programScreenInfo.current);
                break;
            }
            case UIOrchestratorState.RECORDING_RECOVERED: {
                fetchActionsAndRefreshProgramScreen(state);
                dispatch(
                    showWhisper({
                        message: Locale.getString(LocaleKeys.RECOVER_RECORDING_WHISPER_MESSAGE),
                    }),
                );
                break;
            }
            case UIOrchestratorState.FETCHING_DATA:
                dispatch(setScreenTaskController(ScreenTaskControllerTypes.LOADING_STATE_TASK));
                break;
            case UIOrchestratorState.DATA_LOADED:
                dispatch(
                    setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_PROGRAM_SCREEN_TASKS),
                );
                break;

            default:
        }
        handleRecordingStatusUpdate(state);
    };

    const handleRefreshInitialIndex = (state: UIOrchestratorState) => {
        switch (state) {
            case UIOrchestratorState.NEW_SCREEN_MOUNTED:
                break;

            default:
                initialVerticalHighlightIndex.current = 0;
                initialHorizontalHighlightIndex.current = 0;
        }
    };

    const onNetworkStateChangeCallback = () => {
        if (NetworkManager.isNetworkDisconnected()) {
            const service = new NdvrInteractor();
            const message = BrandingUiUtility.formatDescription(
                Locale.getString(LocaleKeys.NO_NETWORK_WHISPER_DESCRIPTION),
                {
                    DEVICE_NAME_SHORT: service.getBrandingUiInfoForKey(
                        BrandingUiInfoKeys.DEVICE_NAME_SHORT,
                    ),
                    BUTTON_NAME_SELECT: DeviceModel.isWebOsPlatform()
                        ? Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_LG)
                        : Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_SAMSUNG),
                },
            );
            dispatch(hideOverlayDialog());
            dispatch(showWhisper({ message }));
        } else {
            dispatch(hideWhisper());
        }
    };

    // eslint-disable-next-line consistent-return
    useEffect(() => {
        if (url.includes('program')) {
            const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
            const {
                id,
                contentType,
                horizontalIndex,
                verticalIndex,
                collapsedVodItemId,
                partnerId,
                contentSupplierPartnerId,
                targetInitialProgramScreenOption,
            } = ScreenController.getInstance().getScreen<TProgramScreenState>().getProperties()!;
            initialHorizontalHighlightIndex.current = horizontalIndex;
            initialVerticalHighlightIndex.current = verticalIndex;
            isInitialCallFromURL.current = true;
            programScreenInfo.current = {
                id,
                contentType,
                partnerId,
                collapsedVodItemId,
                contentSupplierPartnerId,
                targetInitialProgramScreenOption,
            };

            fetchContentScreenDetails(programScreenInfo.current);

            UIOrchestrator.stateChangeListener.subscribe(handleDataRefresh);
            UIOrchestrator.stateChangeListener.subscribe(handleRefreshInitialIndex);
            UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.NEW_SCREEN_MOUNTED);
            const refNetworkManager = NetworkManager.subscribe(onNetworkStateChangeCallback);
            return () => {
                UIOrchestrator.stateChangeListener.cancel(handleDataRefresh);
                UIOrchestrator.stateChangeListener.cancel(handleRefreshInitialIndex);
                NetworkManager.unsubscribe(refNetworkManager);
                CursorManager.unsubscribe(refCursorManager);
                setActionList([]);
                setScreenTitle({ title: '', titleSuffix: '' });
                dispatch(hideWhisper());
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url]);

    const checkRefreshProgramScreen = (
        state: UIOrchestratorState,
        updatedActionList: Array<Detail>,
    ) => {
        // logic to refresh program screen
        if (updatedActionList.length > 0) {
            const validPresentationIds = [
                ContentScreenPresentationId.GET_THIS_SHOW_DETAIL,
                ContentScreenPresentationId.ONE_PASS_OPTIONS_DETAIL,
                ContentScreenPresentationId.RECORDING_OPTIONS_DETAIL,
            ];

            const index = updatedActionList.findIndex((action) =>
                validPresentationIds.includes((action as any)?.presentation?.presentationId),
            );
            const currentDetails = updatedActionList[index];
            const cancelChecker = (currentDetails as any).actions?.filter(
                (action) =>
                    action?.kernel?.actionKernelType ===
                        ActionKernelType.CancelOnePassActionKernelV2 ||
                    action?.kernel?.actionKernelType ===
                        ActionKernelType.CancelMultiOnePassActionKernel,
            );
            if (state === UIOrchestratorState.ONE_PASS_DELETED) {
                if (cancelChecker && cancelChecker.length === 0) {
                    refreshProgramScreen(updatedActionList);
                }
            } else if (state === UIOrchestratorState.ONE_PASS_CREATED) {
                if (
                    cancelChecker &&
                    cancelChecker.length === 1 &&
                    cancelChecker[0].kernel.actionKernelType !==
                        ActionKernelType.CancelMultiOnePassActionKernel
                ) {
                    refreshProgramScreen(updatedActionList);
                }
            } else if (state === UIOrchestratorState.RECORDING_RECOVERED) {
                refreshProgramScreen(updatedActionList);
            }
        }
    };

    const refreshProgramScreen = (updatedActionList) => {
        dispatch(initAndResetBiaxialVerticalStrip(0, updatedActionList.length));
    };

    return (
        <>
            <div className="ps-container">
                <ScreenHeader
                    title={screenTitle.title}
                    titleSuffix={screenTitle.titleSuffix}
                    clockType={ClockTypes.DEFAULT_CLOCK}
                />
                {actionList.length > 0 && (
                    <BiaxialActionWidget
                        contentId={_contentId}
                        collectionId={unifiedCollectionId}
                        actions={actionList}
                        optionsToDisplay={displayOptionNames}
                        initialVerticalHighlightIndex={initialVerticalHighlightIndex.current}
                        initialHorizontalHighlightIndex={initialHorizontalHighlightIndex.current}
                        contentScreenType={layoutType}
                        partnerId={programScreenInfo.current.partnerId}
                        collapsedVodItemId={programScreenInfo.current.collapsedVodItemId}
                        contentSupplierPartnerId={
                            programScreenInfo.current.contentSupplierPartnerId
                        }
                    />
                )}
                {isAntiBurnOsdActive && <AntiburnOsd />}
                {DeviceModel.isWebOsPlatform() && isCursorActive ? <InteractiveLayer /> : null}
            </div>
            <Whisper message="" />
        </>
    );
};

export default ProgramScreen;
